import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuth } from '../auth/AuthContext';
import { authApi } from '../api/endpoints';
import { getApiErrorMessage } from '../api/client';
import { notify } from '../components/Notifier';

/** Zalo của quản trị viên — liên hệ khi quên mật khẩu hiện tại để được cấp lại. */
const ADMIN_ZALO_URL = 'https://zalo.me/0961491332';

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Hộp thoại tự đổi mật khẩu. Tài khoản chưa từng có mật khẩu (Google-only,
 * `user.has_password === false`) không hiện ô "Mật khẩu hiện tại" — đây là
 * lần đặt mật khẩu đầu tiên.
 */
export function ChangePasswordDialog({ open, onClose }: ChangePasswordDialogProps): JSX.Element {
  const { user, refresh } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasPassword = user?.has_password ?? true;

  useEffect(() => {
    if (!open) return;
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
  }, [open]);

  const validate = (): string | null => {
    if (hasPassword && !currentPassword) return 'Vui lòng nhập mật khẩu hiện tại';
    if (newPassword.length < 8) return 'Mật khẩu mới phải có ít nhất 8 ký tự';
    if (newPassword !== confirmPassword) return 'Mật khẩu xác nhận không khớp';
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await authApi.changePassword(hasPassword ? currentPassword : undefined, newPassword);
      await refresh();
      notify('Đã đổi mật khẩu', 'success');
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Không thể đổi mật khẩu'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{hasPassword ? 'Đổi mật khẩu' : 'Đặt mật khẩu'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {hasPassword && (
            <>
              <TextField
                label="Mật khẩu hiện tại"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                fullWidth
                required
                disabled={saving}
              />
              <Alert severity="info">
                Nếu bạn không nhớ mật khẩu hiện tại, hãy liên hệ{' '}
                <Link href={ADMIN_ZALO_URL} target="_blank" rel="noopener noreferrer">
                  quản trị viên
                </Link>
              </Alert>
            </>
          )}
          <TextField
            label="Mật khẩu mới"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            required
            helperText="Tối thiểu 8 ký tự"
            disabled={saving}
          />
          <TextField
            label="Xác nhận mật khẩu mới"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth
            required
            disabled={saving}
          />
          {error && <Box sx={{ color: 'error.main', fontSize: 14 }}>{error}</Box>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Hủy
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {hasPassword ? 'Đổi mật khẩu' : 'Đặt mật khẩu'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
